import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Heart, ThumbsUp, Frown, Flame, MessageSquare } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const REACTIONS = [
  { key: "heart", icon: Heart, label: "Susțin" },
  { key: "support", icon: ThumbsUp, label: "Mulțumesc" },
  { key: "sad", icon: Frown, label: "Trist" },
  { key: "important", icon: Flame, label: "Important" },
];

export const CommentSection = ({ targetType, targetId }) => {
  const { user } = useAuth() || {};
  const [comments, setComments] = useState([]);
  const [body, setBody] = useState("");
  const [anonymous, setAnonymous] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/comments`, { params: { target_type: targetType, target_id: targetId } });
      setComments(data || []);
    } catch {}
  };

  useEffect(() => { if (targetId) load(); /* eslint-disable-next-line */ }, [targetId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error("Pentru a comenta, te rog autentifică-te");
      return;
    }
    if (body.trim().length < 5) {
      toast.error("Comentariul este prea scurt");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/comments", {
        target_type: targetType, target_id: targetId,
        body: body.trim(), is_anonymous: anonymous,
      });
      setBody("");
      toast.success("Comentariu publicat");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la publicare");
    } finally {
      setSubmitting(false);
    }
  };

  const react = async (c, rxn) => {
    if (!user) { toast.error("Autentifică-te pentru a reacționa"); return; }
    try {
      await api.post("/reactions", { target_type: "comment", target_id: c.comment_id, reaction: rxn });
      toast.success("Reacție înregistrată");
    } catch {}
  };

  return (
    <div data-testid="comments-section" className="mt-12">
      <h3 className="font-display text-2xl md:text-3xl text-[#F8F8F4] mb-6 flex items-center gap-3">
        <MessageSquare className="w-6 h-6 text-[#D4A63D]" /> Discuție
      </h3>

      {user ? (
        <form onSubmit={submit} className="cinematic-card rounded-lg p-5 mb-8">
          <textarea
            data-testid="comment-textarea"
            className="w-full bg-[#0A1020] border border-white/10 rounded-sm p-4 text-[#F8F8F4] placeholder-white/30 focus:border-[#D4A63D] outline-none transition-colors"
            rows={3}
            placeholder="Scrie cu empatie și respect. Comentariile sunt moderate."
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
          <div className="mt-3 flex items-center justify-between gap-3">
            <label className="text-sm text-[#A0AABF] flex items-center gap-2 cursor-pointer">
              <input
                data-testid="comment-anonymous-toggle"
                type="checkbox"
                checked={anonymous}
                onChange={(e) => setAnonymous(e.target.checked)}
                className="accent-[#D4A63D]"
              />
              Postează anonim
            </label>
            <button
              data-testid="comment-submit-btn"
              disabled={submitting}
              type="submit"
              className="btn-gold rounded-sm px-5 py-2 text-sm disabled:opacity-50"
            >
              {submitting ? "Se trimite..." : "Publică"}
            </button>
          </div>
        </form>
      ) : (
        <div className="cinematic-card rounded-lg p-5 mb-8 flex items-center justify-between gap-4">
          <p className="text-[#A0AABF] text-sm">Autentifică-te pentru a participa la discuție.</p>
          <Link to="/autentificare" className="btn-gold rounded-sm px-5 py-2 text-sm">Autentificare</Link>
        </div>
      )}

      <div className="space-y-4">
        {comments.length === 0 && (
          <p className="text-[#A0AABF] text-sm italic">Fii primul care comentează.</p>
        )}
        {comments.map(c => (
          <article key={c.comment_id} data-testid={`comment-${c.comment_id}`} className="cinematic-card rounded-lg p-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-9 h-9 rounded-full bg-[#1A2340] grid place-items-center text-[#D4A63D] font-display">
                {(c.user_name || "A")[0]}
              </div>
              <div>
                <div className="text-sm font-medium text-[#F8F8F4]">{c.user_name || "Anonim"}</div>
                <div className="text-xs text-[#6C7A9C]">{new Date(c.created_at).toLocaleString("ro-RO")}</div>
              </div>
            </div>
            <p className="text-[#F8F8F4]/90 text-[15px] leading-relaxed whitespace-pre-line">{c.body}</p>
            <div className="mt-3 flex items-center gap-1 flex-wrap">
              {REACTIONS.map(r => {
                const Icon = r.icon;
                return (
                  <button
                    key={r.key}
                    onClick={() => react(c, r.key)}
                    data-testid={`comment-${c.comment_id}-react-${r.key}`}
                    className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-white/5 hover:bg-white/10 border border-white/5 text-[#A0AABF] hover:text-[#D4A63D] transition-colors"
                  >
                    <Icon className="w-3.5 h-3.5" /> {r.label}
                  </button>
                );
              })}
            </div>
          </article>
        ))}
      </div>
    </div>
  );
};
